import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Clock,
  Image as ImageIcon,
  KeyRound,
  Loader2,
  Mail,
  RotateCcw,
  Save,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldBan,
  SlidersHorizontal,
  Upload,
  Wrench,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FieldLabel } from '../../components/ui/Field'
import { api, extractApiError } from '../../lib/api'
import { CONTAINS_LETTER, CONTAINS_EMOJI } from '../../lib/validators'
import { platformSettingsResponse } from '../../lib/types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { MaintenanceSettings } from '../../components/MaintenanceSettings'
import { IpAccessSettings } from '../../components/IpAccessSettings'
import { AiFirewallSettings } from '../../components/AiFirewallSettings'
import { SmtpSettings } from '../../components/SmtpSettings'
import { GoogleSsoSettings } from '../../components/GoogleSsoSettings'
import { SessionSettings } from '../../components/SessionSettings'
import { SettingsUpdatedAt } from '../../components/SettingsUpdatedAt'

/** Latest of a list of ISO timestamps (ISO 8601 sorts lexically), or null
 *  when the list is empty. Lets a tab backed by several settings rows show
 *  one "Last updated" line for its most-recent change. */
function latestStamp(stamps) {
  return stamps.filter(Boolean).reduce((a, b) => (a && a > b ? a : b), null)
}

/** Key of the platform-logo setting. The Logo Upload tile is rendered
 *  as a dedicated card (file picker + S3-backed URL) instead of the
 *  textarea card the rest of the settings use, so we filter this key
 *  out of the standard grid below. */
const LOGO_SETTING_KEY = 'platform.brand_logo_url'
const WEBSITE_LOGO_SETTING_KEY = 'platform.website_logo_url'
const LOGO_ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp,image/svg+xml'
const LOGO_MAX_BYTES = 2 * 1024 * 1024 // mirror the API cap so we fail fast client-side

/** Logo-card variant configs — keeps the JSX call sites DRY and
 *  makes adding a third logo (favicon, mobile-app icon, etc.) a
 *  one-entry change rather than a fresh JSX block.
 *
 *  Each variant points at its own setting key + REST endpoint pair,
 *  and carries the operator-facing copy (title, audit context line,
 *  consequences-of-removal warning). */
const LOGO_VARIANTS = {
  platform: {
    settingKey: LOGO_SETTING_KEY,
    endpoint: '/super-admin/settings/logo',
    title: 'Platform Logo',
    helperLine:
      'Used in app sidebars, auth screens, error pages, and every email template as {{platform_logo_url}}.',
    // The preview sits on the navy this mark actually ships on: the
    // `.taskmagix-sidebar` gradient's top stop, which is also the email
    // header bar. The platform logo is a WHITE-on-transparent asset, so
    // previewing it on the near-white canvas rendered it all but invisible
    // and made a correctly-uploaded logo look broken (QA, 2026-09-01).
    // AuthLayout already compensates the same way, wrapping <Logo> in a
    // navy chip on the light auth page.
    //
    // Pinned rather than themed: where this logo ships does not change with
    // the admin's own light/dark preference, so neither should the preview.
    previewClass: 'bg-[#061a3a]',
    previewPlaceholderClass: 'text-white/60',
    previewCaption: 'On sidebar navy',
  },
  website: {
    settingKey: WEBSITE_LOGO_SETTING_KEY,
    endpoint: '/super-admin/settings/website-logo',
    title: 'Website Logo',
    helperLine:
      'Used in the marketing website Navbar and Footer. Separate from the Platform Logo so the public site can show a different mark than the panel sidebar.',
    // The marketing site is light in both themes, and this is a DARK mark.
    // Pinned to the light canvas value for the same reason as above — on
    // `--color-canvas` it inverted to #0c0e12 in dark mode and this logo
    // vanished, the exact mirror of the platform-logo defect.
    previewClass: 'bg-[#f5f7fa]',
    previewPlaceholderClass: 'text-[#64748b]',
    previewCaption: 'On website white',
  },
}

/** Settings that have a purpose-built editor rather than the raw
 *  key/value card. They persist as platform-setting rows, so the
 *  `/super-admin/settings` list returns them — but editing them as raw
 *  JSON would bypass their validation (and, for Google SSO, corrupt the
 *  encrypted client-secret envelope), so they're filtered out of the
 *  generic grid.
 *    - `maintenance` → the <MaintenanceSettings /> section below
 *    - `ip_access`   → the <IpAccessSettings /> section below
 *    - `google_sso`  → the Google SSO page (/super-admin/google-sso)
 *    - `usage_report`→ backend-managed config for the scheduled usage
 *                      digest runner; its settings page was removed, so
 *                      it must never surface as a raw-JSON card. */
const PAGE_OWNED_SETTING_KEYS = new Set([
  'maintenance',
  'ip_access',
  'google_sso',
  'usage_report',
  // `lead_ingest` — the inbound-lead secret + toggle. It has its own managed
  // endpoints (token rotate / enable-disable), and editing the raw JSON here
  // could corrupt or leak the secret, so it's hidden from the generic grid.
  'lead_ingest',
])

/** Internal system markers — rows that the server writes for its own
 *  bookkeeping (one-shot migration sentinels, boot-time guards, etc.)
 *  and that human operators should NEVER edit through the UI.
 *
 *  These rows still live in the platform-settings collection (so the
 *  backend can guard against re-running a one-shot script), but
 *  surfacing them in the generic key/value grid invites two problems:
 *    1. Operators see noisy "marker" rows next to real configuration
 *       knobs and can't tell which is which.
 *    2. Hitting "Reset to default" on a backfill marker would set
 *       `done: false` and trigger the migration to re-run on the next
 *       API boot — almost never what the operator intended.
 *
 *  Add a key here when you add a server-side guard that pins itself
 *  to the platform-settings collection but has no operator-facing
 *  edit story.
 *    - `badges_milestone_backfill_v1` — tombstone for the one-shot
 *      milestone-badge backfill (see `seed/backfill-badges.js`). */
const SYSTEM_MARKER_SETTING_KEYS = new Set([
  'badges_milestone_backfill_v1',
  // Visitor-log RETENTION runner state (`{ lastPurgeAt }`) — written by the
  // server to track when it last purged old visitor logs. Not a configuration
  // knob; editing it (or hitting "Reset to default") would perturb the runner's
  // purge window. See jobs/visitor-log-retention.runner.js.
  'visitor_log',
])

/** Turn a setting key like `ai.monthly_quota_per_tenant` into a
 *  human-readable Title Case label like "AI Monthly Quota Per Tenant". */
const ACRONYMS = new Set([
  'ai',
  'api',
  'smtp',
  'url',
  'id',
  'sop',
  'jwt',
  'csv',
  'json',
  'html',
  'css',
  'js',
  'ui',
  'ux',
  'seo',
  'sso',
  'tls',
  'ssl',
])
function titleCaseKey(key) {
  return key
    .split(/[._]+/)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase()
      if (ACRONYMS.has(lower)) return lower.toUpperCase()
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

/** Stringify a setting value for editing. Booleans / numbers / objects all
 *  get encoded so the editor is one-textbox-per-setting. */
function stringifyValue(v) {
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v, null, 2)
}

/** Parse the user's edited string back into JSON-compatible value. Falls
 *  back to a plain string if the parse fails. */
function parseValue(s) {
  const t = s.trim()
  if (t === 'true') return true
  if (t === 'false') return false
  if (t === 'null') return null
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t)
  if (t.startsWith('{') || t.startsWith('[') || t.startsWith('"')) {
    try {
      return JSON.parse(t)
    } catch {
      return s
    }
  }
  return s
}

/** Per-key inline validation, mirroring the server's authoritative guard
 *  (`SETTING_VALIDATORS` in modules.service.js) so the admin sees the
 *  problem before saving instead of getting a server error toast. Takes
 *  the raw editor string; returns an error message or null. Keys with no
 *  rule always pass. */
function settingError(key, draftString) {
  if (key === 'uploads.max_size_mb') {
    const n = Number(String(draftString).trim())
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 100) {
      // 100 MB is the hard ceiling the upload path can serve safely
      // (files are buffered in memory before streaming to S3).
      return 'Enter a whole number between 1 and 100 (MB).'
    }
  }
  if (key === 'platform.name') {
    const v = String(draftString ?? '').trim()
    if (v.length < 2 || v.length > 60) {
      return 'Platform Name must be between 2 and 60 characters.'
    }
    if (!CONTAINS_LETTER.test(v)) {
      return 'Platform Name must contain at least one letter.'
    }
    if (CONTAINS_EMOJI.test(v)) {
      return 'Platform Name cannot contain emojis.'
    }
  }
  if (key === 'platform.support_email') {
    const v = String(draftString ?? '').trim()
    const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (v.length > 320 || !EMAIL.test(v)) {
      return 'Please enter a valid support Email ID.'
    }
  }
  return null
}

/** Card header — icon + title with a thin divider below. Mirrors the
 *  ProfilePage `CardHeader` exactly so Settings cards align visually
 *  with the Personal Information / Change Password cards on the
 *  profile page. */
function CardHeader({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[color:var(--color-rule)]">
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={18} className="text-[color:var(--color-accent-dim)]" />
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 truncate">
          {title}
        </h2>
      </div>
      {right ? (
        <div className="flex items-center gap-2 shrink-0">{right}</div>
      ) : null}
    </div>
  )
}

/** Default / Override / Unsaved status pill. Shared by every section
 *  that needs the same three-state badge. */
function StatusPill({ tone, label }) {
  const styles = {
    accent:
      'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]',
    warning:
      'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]',
    muted:
      'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]',
  }
  return (
    <span
      className={
        'text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border ' +
        (styles[tone] ?? styles.muted)
      }
    >
      {label}
    </span>
  )
}

/** Settings-page tab button. Mirrors the TabButton on the Email
 *  Templates page so the two tabbed platform pages look identical. */
function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium cursor-pointer transition-colors ' +
        (active
          ? 'bg-[color:var(--color-primary)] text-white'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
      }
    >
      <Icon size={14} />
      {label}
    </button>
  )
}

export function SettingsPage() {
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState('general')
  // Branding logos are STAGED here (a File per variant) and only uploaded
  // when the Branding tab's "Save Changes" button is pressed — mirroring the
  // draft-then-save flow of the other tabs.
  const [stagedLogos, setStagedLogos] = useState({
    platform: null,
    website: null,
  })
  const settingsQuery = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const res = await api.get('/super-admin/settings')
      return platformSettingsResponse.parse(res.data)
    },
  })

  // Drafts the user is editing. Keyed by setting key. A setting is
  // considered "dirty" only when its draft differs from the server's
  // current value — typing then un-typing back to the saved value
  // clears the dirty flag automatically.
  const [drafts, setDrafts] = useState({})
  function setDraft(key, value) {
    setDrafts((prev) => ({ ...prev, [key]: value }))
  }

  const allSettings = settingsQuery.data?.data ?? []
  // Each logo URL gets its own card (file picker + preview) so they
  // are pulled out of the textarea grid below.
  const logoSetting = allSettings.find((s) => s.key === LOGO_SETTING_KEY)
  const websiteLogoSetting = allSettings.find(
    (s) => s.key === WEBSITE_LOGO_SETTING_KEY,
  )
  // Filter:
  //   1. logo URLs (platform + website) — rendered by their own
  //      LogoUploadCard instances above
  //   2. page-owned settings — Maintenance / IP Access / Google SSO
  //      have purpose-built editors and we don't want a raw-JSON edit
  //      path bypassing their validation
  //   3. system markers — boot-time / one-shot migration sentinels
  //      written by the server itself, never meant for human edit
  const settings = allSettings.filter(
    (s) =>
      s.key !== LOGO_SETTING_KEY &&
      s.key !== WEBSITE_LOGO_SETTING_KEY &&
      !PAGE_OWNED_SETTING_KEYS.has(s.key) &&
      !SYSTEM_MARKER_SETTING_KEYS.has(s.key),
  )

  // "Last updated" for the two collection-backed tabs: General covers the
  // generic settings; Branding covers the logo rows.
  const generalUpdatedAt = latestStamp([...settings.map((s) => s.updatedAt)])
  const brandingUpdatedAt = latestStamp([
    logoSetting?.updatedAt,
    websiteLogoSetting?.updatedAt,
  ])

  // Which keys actually changed vs. the server's current value.
  const dirtyKeys = settings
    .filter(
      (s) =>
        drafts[s.key] !== undefined &&
        drafts[s.key] !== stringifyValue(s.value),
    )
    .map((s) => s.key)

  // Dirty settings whose edited value fails its per-key rule. Blocks the
  // Save button so an invalid value never reaches the server (which would
  // reject it anyway) — the matching inline error tells the admin why.
  const invalidDirtyKeys = dirtyKeys.filter((key) =>
    settingError(key, drafts[key]),
  )

  const saveAllMutation = useMutation({
    mutationFn: async () => {
      // Build one task per dirty platform-setting. Each task is
      // independent, so they all run concurrently via
      // Promise.allSettled — partial-failure resilient.
      const tasks = dirtyKeys.map((key) => ({
        key,
        run: () =>
          api.put(`/super-admin/settings/${encodeURIComponent(key)}`, {
            value: parseValue(drafts[key]),
          }),
      }))
      const results = await Promise.allSettled(tasks.map((t) => t.run()))
      const failures = []
      const successfulKeys = []
      results.forEach((r, i) => {
        const task = tasks[i]
        if (r.status === 'fulfilled') {
          successfulKeys.push(task.key)
        } else {
          failures.push({ task, reason: r.reason })
        }
      })
      return { successfulKeys, failures }
    },
    onSuccess: ({ successfulKeys, failures }) => {
      if (successfulKeys.length > 0) {
        setDrafts((prev) => {
          const next = { ...prev }
          for (const k of successfulKeys) delete next[k]
          return next
        })
      }
      const totalSaved = successfulKeys.length
      if (totalSaved > 0) {
        toast.success(
          `${totalSaved} setting${totalSaved === 1 ? '' : 's'} saved.`,
        )
      }
      if (failures.length > 0) {
        const first = failures[0]
        toast.error(
          extractApiError(first.reason).message ||
            `${failures.length} setting${failures.length === 1 ? '' : 's'} failed to save.`,
        )
      }
      void qc.invalidateQueries({ queryKey: ['platform-settings'] })
      // Also invalidate the public branding query that <Logo /> reads
      // from so every Logo instance across every layout (sidebars,
      // auth screens, error pages) re-renders with the new URL
      // without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['platform-branding'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Upload every staged Branding logo to its endpoint, then clear the ones
  // that saved. Runs when the Branding tab's "Save Changes" is pressed.
  const saveLogosMutation = useMutation({
    mutationFn: async () => {
      const pending = Object.entries(stagedLogos).filter(([, file]) => file)
      const results = await Promise.allSettled(
        pending.map(async ([variant, file]) => {
          const body = new FormData()
          body.append('file', file)
          await api.post(LOGO_VARIANTS[variant].endpoint, body, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          return variant
        }),
      )
      const saved = []
      const failed = []
      results.forEach((r) =>
        r.status === 'fulfilled' ? saved.push(r.value) : failed.push(r.reason),
      )
      return { saved, failed }
    },
    onSuccess: ({ saved, failed }) => {
      if (saved.length > 0) {
        setStagedLogos((s) => {
          const next = { ...s }
          for (const v of saved) next[v] = null
          return next
        })
        toast.success(
          `${saved.length} logo${saved.length === 1 ? '' : 's'} saved.`,
        )
        void qc.invalidateQueries({ queryKey: ['platform-settings'] })
        void qc.invalidateQueries({ queryKey: ['platform-branding'] })
      }
      if (failed.length > 0) {
        toast.error(extractApiError(failed[0]).message)
      }
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  const logosDirty = Boolean(stagedLogos.platform || stagedLogos.website)

  // Total count of pending changes shown in the global Save button's
  // label.
  const totalDirtyCount = dirtyKeys.length

  return (
    <div>
      <Breadcrumb current="Settings" homePath="/super-admin/dashboard" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <SettingsIcon
              size={22}
              className="text-[color:var(--color-accent-strong)]"
            />
            Settings
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Change platform-wide settings.
          </p>
        </div>
      </div>

      {/* Tabbed sections — only one panel is visible at a time, so the
       *  page stays short instead of stacking every settings block
       *  (General / Branding / Maintenance / IP Access) down one long
       *  column. Drafts live at the page level, so edits survive a tab
       *  switch and the top "Save all changes" button still saves them. */}
      {/* Scroll the tab bar WITHIN its own box on a narrow screen. A row of 8
       *  tabs is wider than a phone viewport; without this the row widened the
       *  whole page, so dragging the tabs scrolled the entire layout sideways
       *  and cut off the left content (mobile QA). max-w-full + overflow-x-auto
       *  keeps the overflow local to the tab bar; the scrollbar is hidden. */}
      <div className="mb-5 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 w-fit">
        <TabButton
          active={activeTab === 'general'}
          onClick={() => setActiveTab('general')}
          icon={SlidersHorizontal}
          label="General"
        />
        <TabButton
          active={activeTab === 'branding'}
          onClick={() => setActiveTab('branding')}
          icon={ImageIcon}
          label="Branding"
        />
        <TabButton
          active={activeTab === 'smtp'}
          onClick={() => setActiveTab('smtp')}
          icon={Mail}
          label="SMTP"
        />
        <TabButton
          active={activeTab === 'googleSso'}
          onClick={() => setActiveTab('googleSso')}
          icon={KeyRound}
          label="Google SSO"
        />
        <TabButton
          active={activeTab === 'session'}
          onClick={() => setActiveTab('session')}
          icon={Clock}
          label="Session"
        />
        <TabButton
          active={activeTab === 'maintenance'}
          onClick={() => setActiveTab('maintenance')}
          icon={Wrench}
          label="Maintenance"
        />
        <TabButton
          active={activeTab === 'ip'}
          onClick={() => setActiveTab('ip')}
          icon={ShieldBan}
          label="IP Access"
        />
        <TabButton
          active={activeTab === 'aiFirewall'}
          onClick={() => setActiveTab('aiFirewall')}
          icon={ShieldAlert}
          label="AI Firewall"
        />
        </div>
      </div>

      {/* General tab header — icon + heading + one-line description on the
       *  left, the scoped Save button on the right. Matches the Maintenance
       *  / IP Access section headers so every tab reads the same. */}
      {activeTab === 'general' ? (
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
              <SlidersHorizontal
                size={20}
                className="text-[color:var(--color-accent-strong)]"
              />
              General
            </h2>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              Platform name, support email, and other defaults.
            </p>
          </div>
          <button
            type="button"
            onClick={() => saveAllMutation.mutate()}
            disabled={
              totalDirtyCount === 0 ||
              invalidDirtyKeys.length > 0 ||
              saveAllMutation.isPending
            }
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saveAllMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} />
                Save Changes
              </>
            )}
          </button>
        </div>
      ) : null}

      {/* Branding tab header — icon + heading + description on the left,
       *  the scoped Save button on the right. */}
      {activeTab === 'branding' ? (
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
              <ImageIcon
                size={20}
                className="text-[color:var(--color-accent-strong)]"
              />
              Branding
            </h2>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              Upload the platform and website logos.
            </p>
          </div>
          <button
            type="button"
            onClick={() => saveLogosMutation.mutate()}
            disabled={!logosDirty || saveLogosMutation.isPending}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saveLogosMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} />
                Save Changes
              </>
            )}
          </button>
        </div>
      ) : null}

      {/* General — generic platform-setting cards. These are exactly what
       *  the top "Save all changes" button manages. 2-column grid on lg+. */}
      {activeTab === 'general' ? (
        settingsQuery.isLoading ? (
          <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
        ) : settingsQuery.isError ? (
          <p className="text-sm text-[color:var(--color-priority-urgent)]">
            {extractApiError(settingsQuery.error).message}
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {settings.map((s) => (
              <SettingCard
                key={s.key}
                setting={s}
                draft={drafts[s.key]}
                onDraftChange={(value) => setDraft(s.key, value)}
              />
            ))}
          </div>
        )
      ) : null}

      {activeTab === 'general' ? (
        <SettingsUpdatedAt at={generalUpdatedAt} />
      ) : null}

      {/* Branding — platform + website logos. Choosing a file stages it
       *  (with a live preview); the "Save Changes" button above uploads
       *  the staged logo(s). */}
      {activeTab === 'branding' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LogoUploadCard
            variant="platform"
            setting={logoSetting}
            staged={stagedLogos.platform}
            onStage={(file) =>
              setStagedLogos((s) => ({ ...s, platform: file }))
            }
          />
          <LogoUploadCard
            variant="website"
            setting={websiteLogoSetting}
            staged={stagedLogos.website}
            onStage={(file) => setStagedLogos((s) => ({ ...s, website: file }))}
          />
        </div>
      ) : null}

      {activeTab === 'branding' ? (
        <SettingsUpdatedAt at={brandingUpdatedAt} />
      ) : null}

      {/* SMTP — mail-server configuration used for every outbound email. */}
      {activeTab === 'smtp' ? <SmtpSettings /> : null}

      {/* Google SSO — Google OAuth credentials + platform-wide toggle. */}
      {activeTab === 'googleSso' ? <GoogleSsoSettings /> : null}

      {/* Session — access + refresh token lifetimes. */}
      {activeTab === 'session' ? <SessionSettings /> : null}

      {/* Maintenance — per-panel toggles + schedule pickers; saves to
       *  its own endpoint via its own Save button. */}
      {activeTab === 'maintenance' ? <MaintenanceSettings /> : null}

      {/* IP Access — region lock + manual allow/deny list; saves to its
       *  own endpoint via its own Save button. */}
      {activeTab === 'ip' ? <IpAccessSettings /> : null}

      {/* AI Firewall — platform-wide screening of AI request content;
       *  saves to its own endpoint via its own Save button. */}
      {activeTab === 'aiFirewall' ? <AiFirewallSettings /> : null}
    </div>
  )
}

/** Logo Upload card — file picker + preview + remove.
 *
 *  Used for both the panel/email logo and the marketing-website
 *  logo. The `variant` prop ('platform' | 'website') picks which
 *  REST endpoint to hit + which copy to display (see LOGO_VARIANTS
 *  at the top of the file). The card body is otherwise identical. */
function LogoUploadCard({ variant = 'platform', setting, staged, onStage }) {
  const config = LOGO_VARIANTS[variant] ?? LOGO_VARIANTS.platform
  const fileInputRef = useRef(null)

  const logoUrl = typeof setting?.value === 'string' ? setting.value : ''
  // Local object-URL preview of the staged (not-yet-uploaded) file, revoked
  // when the file changes or the card unmounts so we don't leak blob URLs.
  const stagedPreview = useMemo(
    () => (staged ? URL.createObjectURL(staged) : null),
    [staged],
  )
  useEffect(() => {
    return () => {
      if (stagedPreview) URL.revokeObjectURL(stagedPreview)
    }
  }, [stagedPreview])
  const previewSrc = stagedPreview || logoUrl

  function handleFileChange(e) {
    const file = e.target.files?.[0]
    // Clear the input so re-picking the same file still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!file) return
    if (file.size > LOGO_MAX_BYTES) {
      toast.error('Logo must be 2 MB or smaller.')
      return
    }
    // Stage the file — the actual upload happens when the Branding tab's
    // "Save Changes" button is pressed (see saveLogosMutation).
    onStage(file)
  }

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
      <CardHeader
        icon={ImageIcon}
        title={config.title}
        right={
          staged ? (
            <StatusPill tone="accent" label="Unsaved" />
          ) : setting?.isOverridden ? (
            <StatusPill tone="warning" label="Override" />
          ) : (
            <StatusPill tone="muted" label="Default" />
          )
        }
      />
      <div className="p-6 space-y-4">
        <div>
          <FieldLabel label="Logo Image" />
          <div className="flex items-center gap-4 flex-wrap">
            {/* Preview slot — fixed-height frame so the layout doesn't
             *  jump. Shows the staged file when one is chosen, otherwise
             *  the currently-saved logo, else a placeholder. */}
            {/* The two cards deliberately preview on DIFFERENT backdrops —
             *  each is the surface its own logo actually ships on. The
             *  caption says which, so the mismatch reads as intentional
             *  rather than as a rendering fault. */}
            <div className="shrink-0">
              <div
                className={
                  'h-20 w-40 rounded-lg border border-[color:var(--color-rule)] flex items-center justify-center overflow-hidden ' +
                  config.previewClass
                }
              >
                {previewSrc ? (
                  <img
                    src={previewSrc}
                    alt={config.title}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className={`text-xs ${config.previewPlaceholderClass}`}>
                    No logo set
                  </span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-[color:var(--color-ink-subtle)]">
                {config.previewCaption}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept={LOGO_ACCEPT}
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
              >
                <Upload size={14} />
                {previewSrc ? 'Change Logo' : 'Choose Logo'}
              </button>
              {staged ? (
                <button
                  type="button"
                  onClick={() => onStage(null)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-[color:var(--color-card)] text-[color:var(--color-ink)] border border-[color:var(--color-rule-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
          <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
            JPG, PNG, WebP or SVG — up to 2 MB. Stored on S3.{' '}
            {config.helperLine}
            {staged ? ' Press Save Changes to apply.' : ''}
          </p>
        </div>
      </div>
    </section>
  )
}


/** Settings that must always hold a value — their card shows a required `*`
 *  on the Value label (and the control carries `aria-required`). Optional
 *  settings (e.g. logo URLs, which may be empty) are deliberately excluded. */
const REQUIRED_SETTING_KEYS = new Set([
  'platform.name',
  'platform.support_email',
  'uploads.max_size_mb',
])

/** Per-setting card. Title-cased key in the header, FieldLabel + input
 *  for the value in the body. Single-line values render as a one-line
 *  input; multi-line / JSON values auto-grow into a textarea up to 8
 *  rows. */
function SettingCard({ setting, draft, onDraftChange }) {
  const qc = useQueryClient()
  const [confirmReset, setConfirmReset] = useState(false)

  const serverValue = stringifyValue(setting.value)
  const value = draft ?? serverValue
  const isDirty = draft !== undefined && draft !== serverValue
  const error = settingError(setting.key, value)
  const required = REQUIRED_SETTING_KEYS.has(setting.key)

  const resetMutation = useMutation({
    mutationFn: async () => {
      await api.delete(
        `/super-admin/settings/${encodeURIComponent(setting.key)}`,
      )
    },
    onSuccess: () => {
      toast.success('Reset to default.')
      setConfirmReset(false)
      onDraftChange(undefined)
      void qc.invalidateQueries({ queryKey: ['platform-settings'] })
      // Also invalidate the public branding query that <Logo /> reads
      // from so every Logo instance across every layout (sidebars,
      // auth screens, error pages) re-renders with the new URL
      // without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['platform-branding'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const fieldId = `setting-${setting.key.replace(/\./g, '-')}`
  // Detect "this value naturally spans more than one line" — JSON
  // objects, arrays, or anything with a literal newline. Single-line
  // string / number / boolean settings render as a one-line input
  // for the cleanest Profile-style form look. Multi-line ones still
  // get a textarea so JSON values remain editable.
  const isMultiline = value.includes('\n') || value.trim().startsWith('{') || value.trim().startsWith('[')
  const rows = Math.min(8, Math.max(2, value.split('\n').length))

  // Swap the neutral border for a danger border when the edited value
  // fails its rule, so the invalid field is obvious at a glance.
  const borderClass = error
    ? 'border-[color:var(--color-danger)] focus:border-[color:var(--color-danger)]'
    : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]'

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
      <CardHeader
        icon={SlidersHorizontal}
        title={titleCaseKey(setting.key)}
        right={
          <>
            {isDirty ? <StatusPill tone="accent" label="Unsaved" /> : null}
            {setting.isOverridden ? (
              <StatusPill tone="warning" label="Override" />
            ) : (
              <StatusPill tone="muted" label="Default" />
            )}
          </>
        }
      />
      <div className="p-6 space-y-4">
        <div>
          <FieldLabel label="Value" htmlFor={fieldId} required={required} />
          {isMultiline ? (
            <textarea
              id={fieldId}
              value={value}
              onChange={(e) => onDraftChange(e.target.value)}
              rows={rows}
              aria-invalid={Boolean(error)}
              aria-required={required || undefined}
              className={`w-full px-3 py-2 rounded-lg border ${borderClass} bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm placeholder:text-[color:var(--color-ink-subtle)] outline-none transition-colors resize-y leading-relaxed font-mono`}
            />
          ) : (
            <input
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => onDraftChange(e.target.value)}
              aria-invalid={Boolean(error)}
              aria-required={required || undefined}
              className={`w-full h-10 px-3 rounded-lg border ${borderClass} bg-[color:var(--color-card)] text-[color:var(--color-ink)] text-sm placeholder:text-[color:var(--color-ink-subtle)] outline-none transition-colors`}
            />
          )}
          {error ? (
            <p
              role="alert"
              className="mt-1.5 text-xs text-[color:var(--color-danger)]"
            >
              {error}
            </p>
          ) : null}
          {setting.description ? (
            <p className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]">
              {setting.description}
            </p>
          ) : null}
        </div>

        {/* Reset-to-default — separate mutation (deletes the override
         *  row) so it doesn't compose with the global Save. Only
         *  rendered when the setting is currently overridden;
         *  otherwise there's nothing to reset. */}
        {setting.isOverridden ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              disabled={resetMutation.isPending}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <RotateCcw size={11} />
              Reset to Default
            </button>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Reset Setting"
        message={`Reset "${setting.key}" to its built-in default? Any override you've saved here will be cleared.`}
        confirmLabel="Reset to Default"
        variant="danger"
        confirming={resetMutation.isPending}
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => resetMutation.mutate()}
      />
    </section>
  )
}
